import React from 'react';
import { Alert, Box, Stack, Typography } from '@mui/material';

const LABELS = {
    ai_review: 'AI review',
    soi_mapping: 'Schedule of Information response',
    voice_detail: 'Report detail',
    voice_transcript: 'Source transcript',
    notes: 'Site notes',
    gia: 'Gross internal area',
    costs: 'Construction cost benchmark',
    planning_history: 'Planning applications',
    planning_details: 'Planning authority and site constraints',
    benchmark_cost_per_sqft: 'AI benchmark (GBP per sq ft)',
    benchmark_range_low: 'Benchmark range - low (GBP per sq ft)',
    benchmark_range_high: 'Benchmark range - high (GBP per sq ft)',
    submitted_implied_cost_per_sqft: 'Submitted cost rate (GBP per sq ft)',
    unclassified_text: 'Notes',
    professional_note: 'Report wording',
    not_applicable: 'Not applicable',
    unknown: 'Not provided'
};

export function readableLabel(value) {
    const text = String(value || '').trim();
    if (LABELS[text]) return LABELS[text];
    const words = text.replace(/[_.]/g, ' ').replace(/\s+/g, ' ');
    return words ? words[0].toUpperCase() + words.slice(1) : 'Proposal';
}

export function proposalTitle(proposal) {
    if (proposal.category === 'email_detail') return proposal.proposed_value?.title || 'Email project detail';
    if (proposal.category === 'soi_mapping') {
        return proposal.display_label || proposal.proposed_value?.label
            || (proposal.semantic_key || proposal.proposed_value?.field_key
                ? readableLabel(proposal.semantic_key || proposal.proposed_value.field_key)
                : `Schedule of Information response ${proposal.proposed_value?.id || ''}`.trim());
    }
    return readableLabel(proposal.category);
}

export function ReadableValue({ value }) {
    if (value == null || value === '') return <Typography variant="body2" color="text.secondary">Not provided</Typography>;
    if (typeof value === 'boolean') return <Typography variant="body2">{value ? 'Yes' : 'No'}</Typography>;
    if (Array.isArray(value)) {
        if (!value.length) return <Typography variant="body2" color="text.secondary">None listed</Typography>;
        return <Box component="ul" sx={{ pl: 2.5, my: 0 }}>
            {value.map((item, index) => <Box component="li" key={index} sx={{ mb: 0.75 }}><ReadableValue value={item} /></Box>)}
        </Box>;
    }
    if (typeof value === 'object') {
        return <Box component="dl" sx={{ m: 0 }}>
            {Object.entries(value).map(([key, entry]) => <Box key={key} sx={{ mb: 1 }}>
                <Typography component="dt" variant="caption" sx={{ color: 'text.secondary', fontWeight: 700 }}>{readableLabel(key)}</Typography>
                <Box component="dd" sx={{ m: 0 }}><ReadableValue value={entry} /></Box>
            </Box>)}
        </Box>;
    }
    return <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>{String(value)}</Typography>;
}

function SoiResponse({ value }) {
    if (!value || typeof value !== 'object') return <ReadableValue value={value} />;
    const selected = ['yes', 'no', 'not_applicable'].filter((key) => value.raw_response?.[key] === true);
    const answer = selected.length ? selected.map(readableLabel).join(', ')
        : { yes: 'Yes', no: 'No', not_applicable: 'Not applicable', conflict: 'Conflicting selections',
            unmapped: 'Could not match this question to the uploaded layout', unknown: 'Not provided' }[value.status];
    const comment = value.comment ?? value.raw_response?.comment;
    return <Stack spacing={1}>
        {value.status === 'conflict' ? <Alert severity="warning">Conflicting answers were selected. Check the source before accepting.</Alert> : null}
        {value.status === 'unmapped' ? <Alert severity="warning">This question could not be matched to the uploaded layout. No answer has been extracted.</Alert> : null}
        <Box>
            <Typography variant="caption" color="text.secondary">Extracted response</Typography>
            {Object.prototype.hasOwnProperty.call(value, 'value')
                ? <ReadableValue value={value.value} />
                : <Typography variant="body2">{answer || 'Not provided'}</Typography>}
        </Box>
        {comment != null && comment !== '' ? <Box>
            <Typography variant="caption" color="text.secondary">Developer comments</Typography>
            <ReadableValue value={comment} />
        </Box> : null}
        {value.attachment_status === 'matched_readable' ? <Typography variant="body2">Attachment: matched to a readable uploaded file. Its contents still require factual review.</Typography> : null}
        {value.attachment_status === 'claimed_unmatched_or_unreadable' ? <Alert severity="warning">Marked as attached, but no matching readable attachment was found.</Alert> : null}
        {value.attachment_status === 'not_claimed' ? <Typography variant="body2" color="text.secondary">Attachment: not marked as attached.</Typography> : null}
        {value.availability_status === 'future_availability' ? <Typography variant="body2">Availability: marked as available shortly, not received yet.</Typography> : null}
        {value.semantic_correction ? <Alert severity="info">{value.semantic_correction}</Alert> : null}
    </Stack>;
}

function ProposalSources({ proposal }) {
    const evidence = Array.isArray(proposal.evidence) ? proposal.evidence : [];
    const locator = proposal.proposed_value?.locator;
    const sources = evidence.length ? evidence : locator ? [locator] : [];
    return sources.length ? <Box sx={{ mt: 1 }}>
        <Typography variant="caption" sx={{ fontWeight: 700 }}>Source</Typography>
        {sources.map((source, index) => {
            const location = { ...locator, ...source };
            const responseCell = locator?.response_cell || source.response_cell;
            const responseCells = proposal.proposed_value?.raw_cells;
            return <Box key={index} sx={{ mb: 0.5 }}>
                {source.source ? <Typography variant="body2" sx={{ overflowWrap: 'anywhere' }}>{source.source}</Typography> : null}
                {location.sheet ? <Typography variant="caption" component="div">Sheet: {location.sheet}</Typography> : null}
                {location.cell ? <Typography variant="caption" component="div">Question cell: {location.cell}</Typography> : null}
                {responseCell ? <Typography variant="caption" component="div">Response cell: {responseCell}</Typography>
                    : responseCells && Object.keys(responseCells).length ? <Typography variant="caption" component="div">Response cells: {Object.keys(responseCells).join(', ')}</Typography> : null}
                {source.sender ? <Typography variant="body2">From: {source.sender}</Typography> : null}
                {source.received_at ? <Typography variant="caption" component="div">Received: {new Date(source.received_at).toLocaleString('en-GB')}</Typography> : null}
                {source.excerpt && proposal.category !== 'email_detail' ? <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>"{source.excerpt}"</Typography> : null}
                {source.url ? <Typography variant="caption" component="div" sx={{ overflowWrap: 'anywhere' }}>{source.url}</Typography> : null}
            </Box>;
        })}
    </Box> : null;
}

export default function DataIntroductionProposal({ proposal }) {
    const value = proposal.proposed_value;
    let content;
    if (proposal.category === 'email_detail') {
        content = <Stack spacing={1}>
            <Box component="blockquote" sx={{ m: 0, pl: 1, borderLeft: '3px solid', borderColor: 'divider' }}>
                <Typography variant="caption" sx={{ fontWeight: 700 }}>Reference text</Typography>
                <ReadableValue value={value?.reference_text} />
            </Box>
            <Box>
                <Typography variant="caption" sx={{ fontWeight: 700 }}>Inferred detail</Typography>
                <ReadableValue value={value?.inferred_detail} />
            </Box>
        </Stack>;
    } else if (proposal.category === 'soi_mapping') {
        content = <SoiResponse value={value} />;
    } else if (proposal.category === 'gia') {
        content = <Typography variant="body2">{String(value)} {proposal.unit || 'm2'} ({proposal.basis || 'GIA'})</Typography>;
    } else if (proposal.category === 'voice_detail' && value?.professional_note) {
        content = <Box>
            {value.topic ? <Typography variant="caption" color="text.secondary">{readableLabel(value.topic)}</Typography> : null}
            <ReadableValue value={value.professional_note} />
            {value.source_excerpt ? <Box component="blockquote" sx={{ mx: 0, mb: 0, mt: 1, pl: 1, borderLeft: '3px solid', borderColor: 'divider' }}>
                <Typography variant="caption" color="text.secondary">Source transcript</Typography>
                <Typography variant="body2">"{value.source_excerpt}"</Typography>
            </Box> : null}
        </Box>;
    } else {
        content = <ReadableValue value={value} />;
    }
    return <Box sx={{ mt: 1 }}>
        {content}
        <ProposalSources proposal={proposal} />
        <Box component="details" sx={{ mt: 1 }}>
            <Typography component="summary" variant="caption" sx={{ cursor: 'pointer', color: 'text.secondary' }}>Technical extraction details</Typography>
            <Box component="pre" sx={{ my: 1, p: 1, maxHeight: 240, overflow: 'auto', whiteSpace: 'pre-wrap', overflowWrap: 'anywhere', backgroundColor: 'action.hover', fontSize: 12 }}>
                {JSON.stringify({ proposed_value: value, target_field: proposal.target_field, rationale: proposal.rationale, evidence: proposal.evidence }, null, 2)}
            </Box>
        </Box>
    </Box>;
}
